import {DrawFrmThing} from "../../../drawfrm/DrawFrmThing";
import {inches, lineBlueHeavy} from "../../../const";
import {FrmShape} from "../../../drawfrm/FrmShape";
import {FrameProfileArgs} from "./ProfileArgs";
import {FrmRefPoint} from "../../../drawfrm/FrmRefPoint";

// Expansion — a two-piece (telescoping) frame. The head piece and the wall
// piece interlock with a small air gap between them so the assembly can expand.
// Geometry mirrors FrameProfileEXP.svg (drawn at ~40.7px/inch).
//
// The two pieces overlap vertically, so the outline can't be traced as a single
// monotonic top-to-bottom path. We trace it as one chain with a single non-drawn
// "pen-up" bridge across the gap (see FrmShapeLine.penUp): the pieces stay linked
// in the point chain (and move together) but render as two separate segments.
//
// Note: because the gap connector is diagonal, a jamb-depth dimension spanning
// the whole profile would not equal the sum of its vertical lines and would
// corrupt the shared jambDepth var. So only the (responsive) jamb face is
// dimensioned here; the depth is fixed at the drawn proportions.
export class FrameProfileEXP extends DrawFrmThing {

    primaryDoorAnchor = new FrmRefPoint();
    // top of the outer face; the frame-profile view aligns a clamshell mullion on this.
    faceAnchor = new FrmRefPoint();

    constructor(args: FrameProfileArgs) {
        super();
        const {detail, jambDepth, jambFace, view, app, left, swingIn, clamshell, hideFaces, hideFaceDims, flipY} = args;

        const shape = new FrmShape(`frameProfile:EXP:${left ? "left" : "right"}`, {
            dimensionMode: view.dimensionMode,
            thing: this,
            app: app,
        });
        shape.strokeColor = lineBlueHeavy;
        shape.horizontalDimensionSide = left ? "left" : "right";
        if (!left) shape.invertX();
        if((!swingIn) !== !!flipY) shape.invertY();

        //   __________            head piece: top return + top face
        //  |          |
        //  |       ___|           steps out to the stop
        //  |      |              <- gap (pieces interlock here)
        //  |   ___|               wall piece: lip + lower stop
        //  |  |
        //  |__|________           bottom face + bottom return

        // head piece (upper)
        const returnH = 0.637 * inches;  // top return, horizontal lip into the wall
        const returnV = 0.638 * inches;  // top return, vertical
        const face = 2 * inches;            // jamb face width
        const upperThroat = 1.492 * inches; // throat from the face down to the stop
        const stopOut = 0.900 * inches;     // step out to the door stop
        const upperStop = 2.636 * inches;   // upper stop face

        const expGap = 0.05 * inches;      // horizontal offset across the gap

        // gap bridge (not drawn)
        const gapY = 0.656 * inches;        // vertical offset across the gap (pieces interlock)

        // wall piece (lower)
        const lowerThroat = 2.174 * inches; // throat down to the bottom face

        // --- head piece: top return then top face ---
        shape.start(0, 0);
        const topReturnHLine = shape.lineTo(-returnH, 0);
        const topReturnVLine = shape.lineTo(0, -returnV);
        if(clamshell) { topReturnHLine.setPenUp(); topReturnVLine.setPenUp(); }
        const topFace = shape.lineTo(face, 0);

        // upper throat then step out to the stop, down the stop face, back to the gap
        const upperThroatLine = shape.lineTo(0, upperThroat).linkDimension(detail.rabbetDepth);
        shape.lineTo(stopOut, 0);
        shape.lineTo(0, upperStop).linkDimension(detail.expFace);

        const expLip = (stopOut - expGap)
        shape.lineTo(-expLip, 0);

        // the door closes against the stop; anchor at the bottom of the wall
        // piece's throat (door extends up from here)
        this.primaryDoorAnchor.setLocalPoint(upperThroatLine.pointB);

        // --- gap: bridge across to the wall piece without drawing it ---
        const gapLine = shape.lineTo(expLip-expGap, -gapY);
        gapLine.setPenUp();

        // --- wall piece: lip, throat, bottom face, bottom return ---
        shape.lineTo(-expLip, 0);
        shape.lineTo(0, lowerThroat).linkDimension(detail.bottomRabbetDepth, "extended");
        const bottomFace = shape.lineTo(-face, 0);
        const bottomReturnVLine = shape.lineTo(0, -returnV);
        const bottomReturnHLine = shape.lineTo(returnH, 0);
        if(clamshell) { bottomReturnVLine.setPenUp(); bottomReturnHLine.setPenUp(); }

        topReturnHLine.dimMatches(bottomReturnHLine);
        topReturnVLine.dimMatches(bottomReturnVLine);

        topFace.linkDimension(jambFace);
        bottomFace.dimMatches(topFace);

        // shape.flagForDebug(topFace.pointA, "A");
        // shape.flagForDebug(bottomFace.pointB, "B");
        // shape.addDimension(topFace.pointA, bottomFace.pointB, jambDepth);

        // clamshell secondary side: omit the top/bottom faces so the primary side's
        // faces cap the merged mullion (avoids a doubled/gapped cap).
        if(hideFaces) { topFace.setPenUp(); bottomFace.setPenUp(); }
        if(hideFaces || hideFaceDims) shape.hideDimensionsForVars([jambFace, jambDepth]);
        this.faceAnchor.setLocalPoint(topFace.getStartpoint());
        shape.done();
    }
}
